/* eslint-disable react-hooks/exhaustive-deps */
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';
import { tupleToStringKey } from 'js/lib/stringKeyTuple';

import { usePlagiarismDetectionContext } from 'bundles/academic-integrity/components/plagiarism-detection/PlagiarismDetectionContext';
import PlagiarismDetectionResult from 'bundles/academic-integrity/components/plagiarism-detection/PlagiarismDetectionResult';
import PlagiarismDetectionResultLayout, {
  RESULT_TYPES,
} from 'bundles/academic-integrity/components/plagiarism-detection/PlagiarismDetectionResultLayout';
import SimilarityChecks, {
  SimilarityChecksStatuses,
} from 'bundles/academic-integrity/components/plagiarism-detection/SimilarityChecks';
import type { SimilarityChecksData } from 'bundles/academic-integrity/components/plagiarism-detection/SimilarityChecks';
import { getErrorMessagesMap } from 'bundles/academic-integrity/components/plagiarism-detection/constants/ErrorMessages';
import {
  SimilarityCheckRoles,
  getUseDraftFromSimilarityCheckRole,
} from 'bundles/academic-integrity/components/plagiarism-detection/utils/SimilarityCheckRoles';
import type { SimilarityCheckRole } from 'bundles/academic-integrity/components/plagiarism-detection/utils/SimilarityCheckRoles';
import CenteredLoadingSpinner from 'bundles/assess-common/components/CenteredLoadingSpinner';

import _t from 'i18n!nls/academic-integrity';

export const ASSIGNMENT_TYPES = {
  STAFF: 'STAFF',
  PEER: 'PEER',
} as const;

export type AssignmentType = typeof ASSIGNMENT_TYPES[keyof typeof ASSIGNMENT_TYPES];

export type InputProps = {
  courseId: string;
  itemId: string;
  authorId: number;
  assignmentType: AssignmentType;
  responseId: string;
  hasContent?: boolean;
  hasUnsavedChanges?: boolean;
  role: SimilarityCheckRole;
  teamId?: string;
};

type PropsFromContainer = {
  isSimilarityCheckDisabled: boolean;
  hasNetworkError: boolean;
  responseId: string;
  role: SimilarityCheckRole;
  loading: boolean;
  refetchReport: () => Promise<{}>;
  submitForCheck: () => Promise<{}>;
};

// 10s, 10s, 20s, 20s, 1min, 1min, 1min
const retryIntervals = [10 * 1000, 10 * 1000, 20 * 1000, 20 * 1000, 60 * 1000, 60 * 1000, 60 * 1000];

/**
 * This hook is used for exponential refetching of the similarity report from turnitin
 * The fetching interval is 10s, 10s, 20s, 20s, 1min, 1min, 1min
 * @param refetch refetch function
 * @param similarityChecksDataRef ref for similarity Check data
 */
const useExponentialRefetching = (
  refetch: () => Promise<{}>,
  similarityChecksDataRef: React.MutableRefObject<SimilarityChecksData | undefined>
) => {
  // exponential fetching
  const waitTimeRef = useRef([...retryIntervals]);
  const { setIsPollingTimedOut } = usePlagiarismDetectionContext();

  const resetWaitTime = () => {
    setIsPollingTimedOut(false);
    waitTimeRef.current = [...retryIntervals];
  };

  const startRefetching = (isManuallyTriggered: boolean) => {
    const { result, status } = similarityChecksDataRef.current || {};

    const waitTime = waitTimeRef.current.splice(0, 1);
    if (waitTime.length === 0 || !!result) {
      if (waitTime.length === 0) {
        setIsPollingTimedOut(true);
      }
      return;
    }
    setTimeout(() => {
      // only refetch when there is no result
      if ((!result && status) || isManuallyTriggered) {
        refetch();
        startRefetching(isManuallyTriggered);
      }
    }, waitTime[0]);
  };

  return { startRefetching, resetWaitTime };
};
/**
 * Component primarily for prompt level results display and updating the prompt plagiarism data
 * in the PlagiarismDetectionContext
 */
export const PlagiarismDetectionContainer: React.FC<PropsFromContainer> = (props) => {
  const { submitForCheck, refetchReport, responseId, isSimilarityCheckDisabled, role, loading, hasNetworkError } =
    props;

  const {
    isReadyForCheck,
    setIsReadyForCheck,
    similarityChecksDataMap,
    setSimilarityChecksDataMap,
    submitForCheckErrorCodeMap,
    setSubmitForCheckErrorCodeMap,
  } = usePlagiarismDetectionContext();

  const similarityChecksData = similarityChecksDataMap[responseId];
  const similarityChecksDataRef = useRef(similarityChecksData);

  // hook for syncing the similarityChecksDataRef
  const { startRefetching, resetWaitTime } = useExponentialRefetching(refetchReport, similarityChecksDataRef);
  useEffect(() => {
    similarityChecksDataRef.current = similarityChecksData;
  }, [similarityChecksData]);

  // hook for triggering the check
  useEffect(() => {
    // this context is toggled once the assignment level check action is triggered
    if (isReadyForCheck && !similarityChecksDataRef.current?.result) {
      setSimilarityChecksDataMap((prevSimilarityChecksDataMap) => ({
        ...prevSimilarityChecksDataMap,
        [responseId]: {
          status: SimilarityChecksStatuses.IN_PROGRESS,
        },
      }));
      submitForCheck()
        .then(() => {
          setIsReadyForCheck(false);
          resetWaitTime();
          // trigger for exponential refetching
          startRefetching(true);
        })
        // In our case, error code 400 is likely an indication of zero length file error
        // handle this networkError in submitForCheckErrorCodeMap to distinguish it from our existing error handling flow
        // see `SimilarityChecksErrors ` in `bundles/academic-integrity/components/plagiarism-detection/SimilarityChecks` for additional info
        .catch((error) => {
          if (error.networkError.statusCode === 400) {
            setIsReadyForCheck(false);
            setSubmitForCheckErrorCodeMap((prevNetworkErrorCode) => ({
              ...prevNetworkErrorCode,
              [responseId]: error.networkError?.result.errorCode,
            }));
          }
        });
    }
  }, [isReadyForCheck]);

  const onRefetchClicked = (): Promise<{}> => {
    setSimilarityChecksDataMap((prevSimilarityChecksDataMap) => ({
      ...prevSimilarityChecksDataMap,
      [responseId]: {
        status: SimilarityChecksStatuses.IN_PROGRESS,
      },
    }));
    return refetchReport();
  };

  // Render the error message for zero length file
  if (submitForCheckErrorCodeMap[responseId] === 'ZERO_LENGTH_FILE') {
    const errorMessagesMap = getErrorMessagesMap();
    return (
      <PlagiarismDetectionResultLayout type={RESULT_TYPES.ERROR} title={_t('Edit your response')}>
        {errorMessagesMap.ZERO_LENGTH_FILE}
      </PlagiarismDetectionResultLayout>
    );
  }

  const isInProgress = similarityChecksDataMap[responseId]?.status === SimilarityChecksStatuses.IN_PROGRESS || loading;

  if (!hasNetworkError && isInProgress && role === SimilarityCheckRoles.GRADER) {
    return <CenteredLoadingSpinner />;
  }

  if (isSimilarityCheckDisabled) {
    return (
      <PlagiarismDetectionResultLayout
        type={RESULT_TYPES.ERROR}
        title={<FormattedMessage message={_t('Plagiarism Detection is not enabled for your program')} />}
      />
    );
  }

  if (hasNetworkError) {
    const errorMessagesMap = getErrorMessagesMap();
    return (
      <PlagiarismDetectionResultLayout
        type={RESULT_TYPES.ERROR}
        title={<FormattedMessage message={_t("We couldn't get a result")} />}
      >
        {role === SimilarityCheckRoles.LEARNER ? errorMessagesMap.NO_RESULT : errorMessagesMap.NO_RESULT_GRADER}
      </PlagiarismDetectionResultLayout>
    );
  }

  const { result, status } = similarityChecksData || {};

  if ((!result && !status) || !status) {
    // 204 no content
    // returned when check for plagiarism has not been requested
    // or when hasContent is false(query would be skipped)
    return null;
  }

  return <PlagiarismDetectionResult result={result} status={status} refetch={onRefetchClicked} />;
};

const PlagiarismDetectionContainerWrapper: React.FC<InputProps> = (props) => {
  const { courseId, itemId, authorId, assignmentType, responseId, hasContent, hasUnsavedChanges, role, teamId } = props;
  const questionResponseId = tupleToStringKey([assignmentType, responseId]);
  const isGraderView = role === SimilarityCheckRoles.GRADER;
  const [enableChecks, setEnableChecks] = useState<boolean>(isGraderView);

  const { setSimilarityChecksDataMap, setSubmitForCheckErrorCodeMap, setHasContentMap } =
    usePlagiarismDetectionContext();
  // `hasNewContent` return True when a new file is successfully uploaded and saved
  const hasNewContent = !hasUnsavedChanges && hasContent;

  // hook for initializing the plagiarism results map
  useEffect(() => {
    setSimilarityChecksDataMap((prevSimilarityChecksDataMap) => ({
      ...prevSimilarityChecksDataMap,
      [responseId]: undefined,
    }));
  }, [responseId]);

  // hook for reset the errorCode when the new content is available
  useEffect(() => {
    if (hasNewContent) {
      setSubmitForCheckErrorCodeMap((prevNetworkErrorCode) => ({
        ...prevNetworkErrorCode,
        [responseId]: undefined,
      }));
      setSimilarityChecksDataMap((prevSimilarityChecksDataMap) => ({
        ...prevSimilarityChecksDataMap,
        [responseId]: undefined,
      }));
      setHasContentMap((prevHasContentMap) => ({
        ...prevHasContentMap,
        [responseId]: true,
      }));
      setEnableChecks(isGraderView);
    }
  }, [hasNewContent]);

  const handleSimilarityChecksDataLoaded = (data?: SimilarityChecksData) => {
    setSimilarityChecksDataMap((prevSimilarityChecksDataMap) => {
      // prevent deletion of result by uncleared timeouts
      if (
        prevSimilarityChecksDataMap[responseId]?.status === SimilarityChecksStatuses.COMPLETED &&
        data?.status !== SimilarityChecksStatuses.COMPLETED
      ) {
        return prevSimilarityChecksDataMap;
      } else {
        return {
          ...prevSimilarityChecksDataMap,
          [responseId]: data,
        };
      }
    });
  };

  const scopedTeamId = teamId && teamId.split('@')[0];
  return (
    <SimilarityChecks
      courseId={courseId}
      authorId={authorId}
      itemId={itemId}
      questionResponseId={questionResponseId}
      scopedTeamId={scopedTeamId}
      skip={!enableChecks}
      useDraft={getUseDraftFromSimilarityCheckRole(role)}
      onSimilarityChecksDataLoaded={handleSimilarityChecksDataLoaded}
    >
      {({ refetchReport, submitForCheck, error, loading, hasNetworkError }) => {
        const handleSubmitForChecks = () => {
          setEnableChecks(true);
          return submitForCheck();
        };

        return (
          <PlagiarismDetectionContainer
            submitForCheck={handleSubmitForChecks}
            refetchReport={refetchReport}
            responseId={responseId}
            role={role}
            loading={loading}
            // the apollo client built in error type does include the statusCode field
            isSimilarityCheckDisabled={(error?.networkError as unknown as { statusCode: number })?.statusCode === 403}
            hasNetworkError={hasNetworkError}
          />
        );
      }}
    </SimilarityChecks>
  );
};

export default PlagiarismDetectionContainerWrapper;
